import { useState, useEffect, useCallback } from "react";
import type { ChatMessage } from "../lib/chat-service";
import { sendChatMessage } from "../lib/chat-service";
import { useAuth } from "./useAuth";
import { logError } from "../lib/logError";

const MESSAGES_KEY = "claims_bot_messages";

/** Load persisted messages from localStorage */
function loadMessages(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(MESSAGES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

/** Save messages to localStorage */
function saveMessages(messages: ChatMessage[]) {
  try {
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(messages));
  } catch {
    // Silently fail if localStorage is full
  }
}

export function useChat() {
  const { user, profile } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>(loadMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    saveMessages(messages);
  }, [messages]);

  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const firstName = profile?.full_name?.split(" ")[0];
      const responseText = await sendChatMessage(
        content,
        user?.email ?? undefined,
        firstName,
        user?.id,
        profile?.can_view_financials ?? false,
        profile?.can_view_vault ?? false,
      );

      const botMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "bot",
        content: responseText,
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (err) {
      setError("Failed to send message. Please try again.");
      logError("[Chat] Failed to send message", err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChat = () => setIsOpen((prev) => !prev);

  /** Clear all messages and start fresh */
  const clearMessages = useCallback(() => {
    setMessages([]);
    localStorage.removeItem(MESSAGES_KEY);
  }, []);

  return {
    messages,
    isLoading,
    isOpen,
    error,
    sendMessage,
    toggleChat,
    setIsOpen,
    clearMessages,
  };
}
